import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  ChevronDown, 
  ChevronUp, 
  AlertTriangle,
  Globe,
  Scale,
  FileText,
  Users,
  Shield,
  CheckCircle2,
  Info,
  HelpCircle,
  ArrowRight,
  Bitcoin,
  Zap,
  Key,
  Link2,
  Layers,
  Network,
  Sparkles,
  ChevronRight,
  Lock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

// Tab configuration
const TABS = [
  { id: 'featured', label: 'Featured', icon: Sparkles },
  { id: 'crypto', label: 'Advanced Crypto', icon: Bitcoin },
  { id: 'estate', label: 'Estate Planning', icon: Scale },
  { id: 'passwords', label: 'Password Managers', icon: Lock },
];

const GuideSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Card className="border border-border mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-6 text-left hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <h3 className="text-lg font-serif font-medium text-foreground">{title}</h3>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        )}
      </button>
      {isOpen && (
        <CardContent className="pt-0 pb-6 px-6">
          <div className="prose prose-sm max-w-none text-foreground/90">
            {children}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

const SubSection = ({ title, children }) => (
  <div className="mb-6">
    <h4 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
      <CheckCircle2 className="w-4 h-4 text-primary" />
      {title}
    </h4>
    <div className="pl-6 text-sm leading-relaxed text-muted-foreground">
      {children}
    </div>
  </div>
);

const BulletList = ({ items }) => (
  <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground">
    {items.map((item, idx) => (
      <li key={idx}>{item}</li>
    ))}
  </ul>
);

const Insights = () => {
  const [activeTab, setActiveTab] = useState('featured');

  const renderTabContent = () => {
    switch (activeTab) {
      case 'featured':
        return <FeaturedTab />;
      case 'crypto':
        return <AdvancedCryptoTab />;
      case 'estate':
        return <EstatePlanningTab />;
      case 'passwords':
        return <PasswordManagersTab />;
      default:
        return <FeaturedTab />;
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="insights-page">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-serif text-foreground mb-2">
            Insights
          </h1>
          <p className="text-muted-foreground">
            Guides, frameworks, and education for estate planning and digital legacy
          </p>
        </div>

        {/* Help Link */}
        <Card className="border border-primary/20 bg-primary/5 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex gap-3 items-center">
                <HelpCircle className="w-5 h-5 text-primary flex-shrink-0" />
                <div>
                  <span className="font-medium text-foreground">Need Help Using Lifely?</span>
                  <span className="text-sm text-muted-foreground ml-2">
                    Check out our Help & Support page
                  </span>
                </div>
              </div>
              <Link to="/help">
                <Button variant="outline" size="sm" data-testid="insights-help-link">
                  View Help
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-border pb-4">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {renderTabContent()}
        </div>

        {/* Disclaimer */}
        <Card className="border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 mt-8">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800 dark:text-amber-300">
                <p className="font-medium mb-1">Important Disclaimer</p>
                <p>
                  These guides are for informational purposes only and do not constitute legal advice. 
                  Please consult with qualified professionals for your specific situation.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

{/* ==================== TAB CONTENT COMPONENTS ==================== */}

const FeaturedTab = () => (
  <div className="space-y-6">
    {/* Featured Article: Decentralisation and the Estate Planning Gap */}
    <div className="flex items-center gap-2 mb-2">
      <span className="text-xs font-medium uppercase tracking-wider text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400 px-3 py-1 rounded-full">
        Featured · Thought Leadership
      </span>
      <span className="text-xs font-medium uppercase tracking-wider text-primary/70 bg-primary/10 px-3 py-1 rounded-full">
        Advanced Crypto
      </span>
    </div>
        
        <Card className="border-2 border-orange-200 dark:border-orange-800/50 bg-gradient-to-br from-orange-50/50 to-amber-50/30 dark:from-orange-950/20 dark:to-amber-950/10">
          <CardContent className="p-8">
            <div className="space-y-8">
              {/* Article Header */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg">
                    <Bitcoin className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-serif font-semibold text-foreground">
                      Decentralisation and the Estate Planning Gap
                    </h2>
                    <p className="text-muted-foreground mt-1">
                      Why crypto continuity requires a new approach
                    </p>
                  </div>
                </div>
              </div>

              {/* Opening Statement */}
              <div className="border-l-4 border-orange-400 pl-6 py-2">
                <p className="text-xl text-foreground/90 font-serif italic">
                  The financial system evolved. Estate planning largely did not.
                </p>
              </div>

              {/* Introduction */}
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  Over the past decade, decentralised networks have fundamentally reshaped how value is stored, 
                  transferred, and controlled. <strong className="text-foreground">Self-custody. Smart contracts. 
                  Validators. Multi-signature governance. Layer 2 scaling. Cross-chain liquidity.</strong>
                </p>
                <p>
                  For the first time in history, individuals can hold and move significant wealth without banks, 
                  brokers, or custodians. But inheritance systems were designed for intermediaries.
                </p>
                <p className="text-lg font-medium text-foreground">
                  That tension creates what we call: <span className="text-orange-600 dark:text-orange-400">The Estate Planning Gap.</span>
                </p>
              </div>

              {/* What Traditional Systems Assume */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <Scale className="w-5 h-5 text-muted-foreground" />
                  What Traditional Estate Systems Assume
                </h3>
                <p className="text-muted-foreground">
                  Most inheritance frameworks assume that assets are:
                </p>
                <div className="grid md:grid-cols-2 gap-3">
                  {[
                    "Held by identifiable institutions",
                    "Registered in legal ownership records",
                    "Transferable through court-supervised processes",
                    "Recoverable via documentation",
                    "Mediated by custodians"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-muted-foreground">
                  If someone dies, becomes incapacitated, or disappears, there is usually a third party that can 
                  help reconstruct access. <strong className="text-foreground">Decentralised systems remove that third party.</strong>
                </p>
              </div>

              {/* What Decentralisation Changes */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <Key className="w-5 h-5 text-orange-500" />
                  What Decentralisation Actually Changes
                </h3>
                <div className="bg-foreground/5 rounded-xl p-6 space-y-3">
                  {[
                    { label: "Control", value: "Possession of private keys" },
                    { label: "Ownership", value: "Cryptographic, not institutional" },
                    { label: "Transactions", value: "Irreversible" },
                    { label: "Recovery", value: "Mechanisms may not exist" },
                    { label: "Operator", value: "May be no identifiable operator" }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-orange-500 font-medium min-w-[100px]">{item.label}</span>
                      <span className="text-muted-foreground">= {item.value}</span>
                    </div>
                  ))}
                </div>
                <p className="text-muted-foreground">
                  This is not a flaw. <strong className="text-foreground">It is the design.</strong> But it creates 
                  a structural mismatch with legal processes that depend on recoverability.
                </p>
              </div>

              {/* The Gap Explained */}
              <div className="bg-red-50 dark:bg-red-950/20 rounded-xl p-6 border border-red-200 dark:border-red-800">
                <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  The Gap in Simple Terms
                </h3>
                <div className="space-y-3 text-muted-foreground">
                  <p>A court can declare who inherits an asset. <strong className="text-foreground">A court cannot reconstruct a lost private key.</strong></p>
                  <p>A will can name a beneficiary. <strong className="text-foreground">A will cannot sign a blockchain transaction.</strong></p>
                  <p className="text-lg font-medium text-red-700 dark:text-red-400 pt-2">
                    Legal authority does not equal cryptographic control. That is the estate planning gap.
                  </p>
                </div>
              </div>

              {/* Five Structural Pressure Points */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-foreground">
                  The Five Structural Pressure Points
                </h3>

                {/* Pressure Point 1 */}
                <div className="space-y-3">
                  <h4 className="font-medium text-foreground flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 flex items-center justify-center text-sm font-bold">1</span>
                    Self-Custody Without Continuity
                  </h4>
                  <p className="text-muted-foreground pl-9">
                    Self-custody empowers individuals. It also concentrates operational risk. If seed phrases are 
                    undocumented, hardware wallet processes are unclear, passphrase usage is unknown, or wallets are 
                    fragmented across chains - <strong className="text-foreground">assets may become permanently inaccessible.</strong> 
                    The law cannot compel a blockchain to unlock funds.
                  </p>
                </div>

                {/* Pressure Point 2 */}
                <div className="space-y-3">
                  <h4 className="font-medium text-foreground flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 flex items-center justify-center text-sm font-bold">2</span>
                    Validators as Infrastructure
                  </h4>
                  <p className="text-muted-foreground pl-9">
                    Running a validator is not passive ownership - <strong className="text-foreground">it is operational infrastructure.</strong> 
                    If nodes go offline, slashing protection databases are lost, exit procedures are undocumented, or 
                    withdrawal credentials are misunderstood, financial loss may occur even if the underlying asset still exists. 
                    Estate systems were not built for active protocol participation.
                  </p>
                </div>

                {/* Pressure Point 3 */}
                <div className="space-y-3">
                  <h4 className="font-medium text-foreground flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 flex items-center justify-center text-sm font-bold">3</span>
                    Multi-Signature Governance Risk
                  </h4>
                  <p className="text-muted-foreground pl-9">
                    Multi-sig wallets distribute authority but require signer coordination. If one signer becomes unavailable, 
                    loses access, or is legally restricted - <strong className="text-foreground">the wallet may freeze.</strong> Now 
                    layer in cross-border signers, DAO governance, and smart contract upgrades. The legal system can appoint an 
                    executor. It cannot override a 3-of-5 signature threshold.
                  </p>
                </div>

                {/* Pressure Point 4 */}
                <div className="space-y-3">
                  <h4 className="font-medium text-foreground flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 flex items-center justify-center text-sm font-bold">4</span>
                    Cross-Chain Fragmentation
                  </h4>
                  <p className="text-muted-foreground pl-9">
                    Modern portfolios are rarely simple. Assets may exist across Layer 1 chains, Layer 2 rollups, bridges, 
                    wrapped representations, restaking protocols, and yield aggregators. <strong className="text-foreground">
                    Visibility becomes non-trivial.</strong> Without structured mapping, heirs may not even know where to look. 
                    Traditional estate processes assume centralised account statements. DeFi has no consolidated statement.
                  </p>
                </div>

                {/* Pressure Point 5 */}
                <div className="space-y-3">
                  <h4 className="font-medium text-foreground flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 flex items-center justify-center text-sm font-bold">5</span>
                    Irreversibility as a Feature
                  </h4>
                  <p className="text-muted-foreground pl-9">
                    In legacy finance, fraud can be reversed, errors can be mediated, transfers can be frozen. On most blockchains, 
                    <strong className="text-foreground"> finality is permanent.</strong> Mistakes in asset handling during inheritance 
                    are not easily undone. This amplifies the cost of confusion.
                  </p>
                </div>
              </div>

              {/* Why This Problem is Accelerating */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  Why This Problem is Accelerating
                </h3>
                <p className="text-muted-foreground">
                  Three macro trends intensify the gap:
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-foreground/5 rounded-lg p-4">
                    <div className="font-medium text-foreground mb-1">Growing Adoption</div>
                    <p className="text-sm text-muted-foreground">Self-custody is becoming mainstream</p>
                  </div>
                  <div className="bg-foreground/5 rounded-lg p-4">
                    <div className="font-medium text-foreground mb-1">Increasing Complexity</div>
                    <p className="text-sm text-muted-foreground">DeFi protocols multiply daily</p>
                  </div>
                  <div className="bg-foreground/5 rounded-lg p-4">
                    <div className="font-medium text-foreground mb-1">Younger Holders</div>
                    <p className="text-sm text-muted-foreground">Accumulating significant on-chain wealth</p>
                  </div>
                </div>
                <p className="text-muted-foreground">
                  Many early crypto adopters are now running infrastructure, holding substantial value, participating 
                  in governance, and building digital-native businesses. <strong className="text-foreground">Estate 
                  planning frameworks have not evolved at the same pace.</strong>
                </p>
              </div>

              {/* The Psychological Layer */}
              <div className="bg-blue-50 dark:bg-blue-950/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
                <h3 className="text-xl font-semibold text-foreground mb-4">
                  The Psychological Layer
                </h3>
                <p className="text-muted-foreground mb-4">
                  Crypto culture often emphasises sovereignty, privacy, trust minimisation, and anti-intermediation. 
                  These values are valid. But sovereignty without continuity planning can unintentionally harm the 
                  very people sovereignty is meant to protect.
                </p>
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 bg-white dark:bg-foreground/5 rounded-lg p-4">
                    <div className="font-medium text-foreground mb-1 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-blue-500" />
                      Security
                    </div>
                    <p className="text-sm text-muted-foreground">Protects <strong>you</strong></p>
                  </div>
                  <div className="flex-1 bg-white dark:bg-foreground/5 rounded-lg p-4">
                    <div className="font-medium text-foreground mb-1 flex items-center gap-2">
                      <Users className="w-4 h-4 text-blue-500" />
                      Continuity
                    </div>
                    <p className="text-sm text-muted-foreground">Protects <strong>those who depend on you</strong></p>
                  </div>
                </div>
                <p className="text-sm text-blue-700 dark:text-blue-400 mt-4 font-medium">
                  They are not the same thing.
                </p>
              </div>

              {/* The Emerging Responsibility */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">
                  The Emerging Responsibility
                </h3>
                <p className="text-muted-foreground">
                  If you choose decentralisation, you inherit additional responsibility. Not just for key management, 
                  operational security, and smart contract risk, but for:
                </p>
                <div className="grid md:grid-cols-2 gap-3">
                  {[
                    "Documentation clarity",
                    "Access mapping",
                    "Governance explanation",
                    "Recovery pathway visibility"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-orange-500 flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-muted-foreground font-medium">
                  Decentralisation transfers institutional responsibility to the individual. 
                  <strong className="text-foreground"> Estate planning must adapt accordingly.</strong>
                </p>
              </div>

              {/* Bridging the Gap */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-green-500" />
                  Bridging the Gap: Structured Clarity
                </h3>
                <p className="text-muted-foreground">
                  Closing the estate planning gap does not require sacrificing security. 
                  <strong className="text-foreground"> It requires separation of layers:</strong>
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-red-50 dark:bg-red-950/20 rounded-xl p-5 border border-red-200 dark:border-red-800">
                    <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                      <Key className="w-4 h-4 text-red-500" />
                      Layer 1: Secret Storage
                    </h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• Seed phrases</li>
                      <li>• Private keys</li>
                      <li>• Master passwords</li>
                      <li>• Hardware wallets</li>
                    </ul>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-3 font-medium">
                      These belong in secure, dedicated environments
                    </p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-950/20 rounded-xl p-5 border border-green-200 dark:border-green-800">
                    <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                      <Layers className="w-4 h-4 text-green-500" />
                      Layer 2: Structured Context
                    </h4>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• Wallet types & chains used</li>
                      <li>• Validator setup & multi-sig thresholds</li>
                      <li>• Exchange accounts & bridge usage</li>
                      <li>• Smart contract dependencies</li>
                    </ul>
                    <p className="text-xs text-green-600 dark:text-green-400 mt-3 font-medium">
                      This belongs in a structured clarity system
                    </p>
                  </div>
                </div>
                <p className="text-muted-foreground text-center font-medium pt-2">
                  Without Layer 2, Layer 1 may never be found or understood.
                </p>
              </div>

              {/* Legal Authority vs Technical Capability */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">
                  Legal Authority vs Technical Capability
                </h3>
                <p className="text-muted-foreground">
                  A common misconception: <em>"My executor will figure it out."</em>
                </p>
                <p className="text-muted-foreground">
                  Executors are rarely protocol engineers, DeFi analysts, or validator operators. Even if legally 
                  authorised, they may lack the technical knowledge to safely interact with complex on-chain structures.
                </p>
                <div className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-800 dark:text-amber-300 font-medium mb-2">
                    Clarity reduces the risk of:
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm text-amber-700 dark:text-amber-400">
                    <span>• Accidental token approvals</span>
                    <span>• Wrong-network transfers</span>
                    <span>• Failed bridge interactions</span>
                    <span>• Slashing penalties</span>
                    <span>• Frozen multi-sig funds</span>
                  </div>
                </div>
              </div>

              {/* The Role of Lifely */}
              <div className="bg-primary/5 rounded-xl p-6 border border-primary/20">
                <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  The Role of Lifely
                </h3>
                <p className="text-muted-foreground mb-4">
                  Lifely does not replace legal advice. It does not store private keys. It does not execute transactions.
                </p>
                <p className="text-muted-foreground mb-4">
                  Its role is narrower, and critical: <strong className="text-foreground">To provide structured clarity 
                  in a decentralised world.</strong>
                </p>
                <p className="text-muted-foreground">
                  By documenting what exists, where it exists, how it functions, what depends on it, and what your 
                  intent is - <strong className="text-foreground">you reduce ambiguity without reducing sovereignty.</strong>
                </p>
              </div>

              {/* A Simple Question */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">
                  A Simple Question
                </h3>
                <p className="text-muted-foreground">
                  If your digital footprint vanished tomorrow, could someone you trust:
                </p>
                <div className="space-y-2 pl-4">
                  {[
                    "Identify your on-chain exposure?",
                    "Understand validator obligations?",
                    "Locate multi-sig arrangements?",
                    "Distinguish custodial from non-custodial holdings?",
                    "Know where recovery pathways begin?"
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2 text-muted-foreground">
                      <span className="text-orange-500">→</span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-lg font-medium text-orange-600 dark:text-orange-400 pt-2">
                  If not, the gap exists.
                </p>
              </div>

              {/* Final Thought */}
              <div className="border-t border-border pt-6 space-y-4">
                <h3 className="text-xl font-semibold text-foreground">
                  Final Thought
                </h3>
                <p className="text-muted-foreground">
                  Decentralisation gives individuals unprecedented control. But control without continuity planning 
                  creates fragility.
                </p>
                <p className="text-muted-foreground">
                  Estate planning must evolve from <em>"Who gets what?"</em> to <strong className="text-foreground">"Who 
                  understands how?"</strong>
                </p>
                <div className="bg-foreground text-background rounded-xl p-6 mt-4">
                  <p className="text-lg font-serif text-center">
                    The future of wealth is programmable.<br/>
                    <strong>Its continuity must be intentional.</strong>
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
  </div>
);

const AdvancedCryptoTab = () => (
  <div className="space-y-4">
    <p className="text-muted-foreground text-sm mb-4">
      Deep dives into specific crypto continuity challenges. These guides are designed for users with 
      existing crypto knowledge who need structured approaches to documentation.
    </p>

    {/* Article 1: Running a Validator */}
    <GuideSection title="Running a Validator: What Happens If You're Not There?" icon={Network}>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs font-medium uppercase tracking-wider text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400 px-2 py-0.5 rounded-full">
              Advanced Crypto
            </span>
            <span className="text-xs text-muted-foreground">8 min read</span>
          </div>

          <SubSection title="Introduction">
            <p className="text-lg font-medium text-foreground mb-3">
              A validator is not just an asset. It's an operating system.
            </p>
            <p className="text-muted-foreground">
              If you run a validator (Ethereum or otherwise), you're not simply holding tokens - you are running 
              infrastructure. Infrastructure requires maintenance. If that maintenance stops abruptly, consequences can follow.
            </p>
          </SubSection>

          <SubSection title="What Actually Needs to Be Documented">
            <p className="text-muted-foreground mb-3">
              Most people only document the asset (e.g. "32 ETH staked"). <strong className="text-foreground">That is incomplete.</strong>
            </p>
            <p className="text-muted-foreground mb-3">You should document:</p>
            <BulletList items={[
              "Network (e.g. Ethereum mainnet, testnet, other PoS chain)",
              "Validator client software (e.g. Prysm, Lighthouse, etc.)",
              "Hosting environment (home server, VPS, Dappnode, cloud provider)",
              "Withdrawal credentials type (0x00 or 0x01 format for Ethereum)",
              "Where signing keys are stored",
              "Slashing protection database location",
              "Whether MEV relay is configured",
              "Whether restaking or additional layers are involved"
            ]} />
            <p className="text-sm text-muted-foreground mt-3 italic">
              Not keys. Not seed phrases. Just structured technical context.
            </p>
          </SubSection>

          <SubSection title="Slashing and Inactivity Risk">
            <div className="bg-red-50 dark:bg-red-950/20 rounded-lg p-4 border border-red-200 dark:border-red-800 mb-4">
              <p className="text-sm text-red-800 dark:text-red-300">
                If a validator goes offline: You lose rewards. You may incur inactivity penalties. In some cases, 
                <strong> you can be slashed.</strong> Slashing is not just "lost yield." It is loss of principal.
              </p>
            </div>
            <p className="text-muted-foreground mb-3">Your executor likely does not understand:</p>
            <BulletList items={[
              "What triggers slashing",
              "How to voluntarily exit",
              "How long exit queues may take",
              "How to access withdrawal credentials"
            ]} />
            <p className="text-muted-foreground mt-3">
              Without documentation, the validator may sit inactive for weeks or months.
            </p>
          </SubSection>

          <SubSection title="Voluntary Exit Instructions">
            <p className="text-muted-foreground mb-3">Document clearly:</p>
            <BulletList items={[
              "Whether you want the validator exited",
              "Where exit commands are located",
              "Who can perform them",
              "Whether hardware access is required"
            ]} />
            <p className="text-muted-foreground mt-3 font-medium">
              Clarity prevents unnecessary financial damage.
            </p>
          </SubSection>

          <SubSection title="The Lifely Approach">
            <p className="text-muted-foreground mb-3">In Lifely:</p>
            <BulletList items={[
              "Categorise the validator under Digital Infrastructure",
              "Flag as 'Operational Asset'",
              "Record hosting environment",
              "Record exit intention",
              "Document dependencies (internet, server, power, third-party relays)"
            ]} />
            <p className="text-muted-foreground mt-3">
              Treat it like documenting a small business, because it is.
            </p>
            <div className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-3 border border-amber-200 dark:border-amber-800 mt-4">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                <strong>Important:</strong> Validator mechanics vary by network. Protocol upgrades change risk and 
                configuration requirements. Always verify technical procedures with current network documentation.
              </p>
            </div>
          </SubSection>
        </GuideSection>

        {/* Article 2: Multi-Signature Wallets */}
        <GuideSection title="Multi-Signature Wallets: Security or Governance Risk?" icon={Users}>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs font-medium uppercase tracking-wider text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400 px-2 py-0.5 rounded-full">
              Advanced Crypto
            </span>
            <span className="text-xs text-muted-foreground">7 min read</span>
          </div>

          <SubSection title="Introduction">
            <p className="text-lg font-medium text-foreground mb-3">
              Multi-sig wallets increase security. They also introduce coordination risk.
            </p>
          </SubSection>

          <SubSection title="What a Multi-Sig Actually Is">
            <p className="text-muted-foreground mb-3">
              A multi-signature wallet requires multiple private keys to approve transactions.
            </p>
            <p className="text-muted-foreground mb-2">Common setups:</p>
            <div className="flex gap-3 mb-3">
              <span className="bg-foreground/5 px-3 py-1 rounded text-sm">2-of-3</span>
              <span className="bg-foreground/5 px-3 py-1 rounded text-sm">3-of-5</span>
              <span className="bg-foreground/5 px-3 py-1 rounded text-sm">4-of-7</span>
            </div>
            <p className="text-muted-foreground mb-2">Often used for:</p>
            <BulletList items={[
              "Treasury management",
              "DAO governance",
              "Family holdings",
              "Business reserves"
            ]} />
          </SubSection>

          <SubSection title="The Hidden Risk: Signer Availability">
            <div className="bg-red-50 dark:bg-red-950/20 rounded-lg p-4 border border-red-200 dark:border-red-800 mb-4">
              <p className="text-sm text-red-800 dark:text-red-300">
                If one signer loses access, becomes unreachable, or is incapacitated - <strong>the wallet may 
                become frozen.</strong> It depends entirely on configuration.
              </p>
            </div>
          </SubSection>

          <SubSection title="What Should Be Documented">
            <BulletList items={[
              "Signature threshold (e.g. 2-of-3)",
              "Identity of co-signers",
              "Jurisdiction of each signer",
              "Hardware or software wallet type used",
              "Backup signer arrangements",
              "Whether there is a recovery mechanism",
              "Smart contract platform (e.g. Safe / Gnosis Safe)"
            ]} />
            <p className="text-muted-foreground mt-3 font-medium">
              Executors cannot reverse engineer this.
            </p>
          </SubSection>

          <SubSection title="Governance Clarity Matters">
            <p className="text-muted-foreground mb-3">If the multi-sig is shared:</p>
            <BulletList items={[
              "Is it jointly owned?",
              "Are funds proportionally owned?",
              "Is it a treasury?",
              "Is it custody for someone else?"
            ]} />
            <p className="text-muted-foreground mt-3">
              <strong className="text-foreground">Ambiguity creates dispute.</strong> Document intent clearly.
            </p>
          </SubSection>

          <SubSection title="The Lifely Approach">
            <p className="text-muted-foreground mb-3">In Lifely:</p>
            <BulletList items={[
              "Categorise as Shared Digital Asset",
              "Flag governance complexity",
              "Record threshold structure",
              "Record co-signer details (not keys)",
              "Document your understanding of ownership"
            ]} />
            <p className="text-lg font-medium text-foreground mt-4">
              Security without clarity is fragility.
            </p>
            <div className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-3 border border-amber-200 dark:border-amber-800 mt-4">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                <strong>Important:</strong> Smart contract risks, platform upgrades, and key management standards 
                evolve. Always confirm current best practice.
              </p>
            </div>
          </SubSection>
        </GuideSection>

        {/* Article 3: Cross-Chain Risk */}
        <GuideSection title="Cross-Chain Risk: When Assets Exist Everywhere" icon={Link2}>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs font-medium uppercase tracking-wider text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400 px-2 py-0.5 rounded-full">
              Advanced Crypto
            </span>
            <span className="text-xs text-muted-foreground">8 min read</span>
          </div>

          <SubSection title="Introduction">
            <p className="text-muted-foreground mb-3">
              Modern crypto holders rarely stay on one chain.
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              {["Ethereum", "Layer 2", "Solana", "Base", "Arbitrum", "Cosmos", "Restaking protocols"].map((chain, i) => (
                <span key={i} className="bg-foreground/5 px-3 py-1 rounded text-sm text-muted-foreground">{chain}</span>
              ))}
            </div>
            <p className="text-lg font-medium text-foreground">
              Fragmentation creates visibility risk.
            </p>
          </SubSection>

          <SubSection title="The Documentation Problem">
            <p className="text-muted-foreground mb-3">
              If someone searched your main wallet, would they find:
            </p>
            <BulletList items={[
              "Layer 2 balances?",
              "Bridged assets?",
              "Wrapped tokens?",
              "Restaked positions?",
              "Liquidity pool deposits?"
            ]} />
            <p className="text-muted-foreground mt-3">
              Often, the answer is no.
            </p>
          </SubSection>

          <SubSection title="Bridging Adds Complexity">
            <p className="text-muted-foreground mb-3">When you bridge assets:</p>
            <BulletList items={[
              "The original chain locks tokens",
              "A representation appears on another chain",
              "If someone doesn't understand bridging mechanics, they may believe assets are missing"
            ]} />
            <p className="text-muted-foreground mt-3 mb-2">Document:</p>
            <BulletList items={[
              "Bridge used",
              "Destination chain",
              "Wrapped token format",
              "Where the bridge contract lives"
            ]} />
          </SubSection>

          <SubSection title="Restaking & Composability Risk">
            <p className="text-muted-foreground mb-3">Some positions depend on:</p>
            <BulletList items={[
              "Smart contracts layered on smart contracts",
              "Liquid staking derivatives",
              "Yield aggregators",
              "Rehypothecation structures"
            ]} />
            <p className="text-muted-foreground mt-3">
              These structures are powerful, but opaque. Your documentation should clarify:
            </p>
            <BulletList items={[
              "Base asset",
              "Layered exposure",
              "Smart contract platform",
              "Whether manual unwinding is required"
            ]} />
          </SubSection>

          <SubSection title="The Lifely Approach">
            <p className="text-muted-foreground mb-3">Create a Digital Asset Map:</p>
            <BulletList items={[
              "List primary wallets",
              "List secondary chains",
              "Flag wrapped assets",
              "Identify smart contract exposure",
              "Mark high complexity items"
            ]} />
            <p className="text-lg font-medium text-foreground mt-4">
              Your executor should not need to understand DeFi, just where to start.
            </p>
            <div className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-3 border border-amber-200 dark:border-amber-800 mt-4">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                <strong>Important:</strong> Cross-chain mechanics, bridges, and smart contracts introduce additional 
                counterparty and code risk. Always validate current protocol safety and security assumptions.
              </p>
            </div>
          </SubSection>
        </GuideSection>

        {/* Article 4: Custodial vs Non-Custodial */}
        <GuideSection title="Custodial vs Non-Custodial: The False Sense of Safety" icon={Shield}>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs font-medium uppercase tracking-wider text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400 px-2 py-0.5 rounded-full">
              Advanced Crypto
            </span>
            <span className="text-xs text-muted-foreground">6 min read</span>
          </div>

          <SubSection title="Introduction">
            <p className="text-lg font-medium text-foreground mb-3">
              "It's on an exchange" does not mean it is automatically accessible.
            </p>
            <p className="text-muted-foreground">Custodial assets rely on:</p>
            <BulletList items={[
              "Account access",
              "Platform solvency",
              "Jurisdictional compliance",
              "Identity verification"
            ]} />
          </SubSection>

          <SubSection title="What Should Be Documented">
            <p className="text-muted-foreground mb-3">For each custodial platform:</p>
            <BulletList items={[
              "Platform name",
              "Jurisdiction",
              "KYC status",
              "Email used",
              "2FA method",
              "Linked bank account",
              "Withdrawal whitelist settings",
              "Whether staking is active"
            ]} />
            <p className="text-muted-foreground mt-3 font-medium">
              Do not store passwords. <strong className="text-foreground">Store context.</strong>
            </p>
          </SubSection>

          <SubSection title="Exchange Processes Vary">
            <p className="text-muted-foreground mb-3">Some exchanges:</p>
            <BulletList items={[
              "Require death certificates",
              "Require probate documents",
              "Have strict claim processes",
              "Freeze accounts automatically"
            ]} />
            <p className="text-muted-foreground mt-3">Your family should know:</p>
            <BulletList items={[
              "Which exchanges are used",
              "That they exist",
              "That documentation may be required"
            ]} />
          </SubSection>

          <SubSection title="The Solvency Risk Factor">
            <div className="bg-red-50 dark:bg-red-950/20 rounded-lg p-4 border border-red-200 dark:border-red-800 mb-4">
              <p className="text-sm text-red-800 dark:text-red-300 mb-2">
                Custodial risk includes:
              </p>
              <ul className="text-sm text-red-700 dark:text-red-400 space-y-1">
                <li>• Bankruptcy</li>
                <li>• Regulatory shutdown</li>
                <li>• Frozen withdrawals</li>
              </ul>
            </div>
            <p className="text-muted-foreground">
              If assets are material, diversification should be documented.
            </p>
          </SubSection>

          <SubSection title="The Lifely Approach">
            <p className="text-muted-foreground mb-3">In Lifely:</p>
            <BulletList items={[
              "Separate custodial vs self-custody",
              "Flag exchange exposure",
              "Document required verification steps",
              "Note if balances are staked"
            ]} />
            <p className="text-lg font-medium text-foreground mt-4">
              Clarity reduces panic.
            </p>
            <div className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-3 border border-amber-200 dark:border-amber-800 mt-4">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                <strong>Important:</strong> Exchange policies and regulations change frequently. Always review 
                official platform documentation.
              </p>
            </div>
          </SubSection>
        </GuideSection>

        {/* Article 5: The Irreversibility Problem */}
        <GuideSection title="The Irreversibility Problem: Why Crypto Requires Structured Clarity" icon={AlertTriangle}>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs font-medium uppercase tracking-wider text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400 px-2 py-0.5 rounded-full">
              Advanced Crypto
            </span>
            <span className="text-xs text-muted-foreground">7 min read</span>
          </div>

          <SubSection title="Introduction">
            <p className="text-lg font-medium text-foreground mb-3">
              Crypto transactions are irreversible. That is a feature.
            </p>
            <p className="text-muted-foreground">
              But irreversibility magnifies mistakes.
            </p>
          </SubSection>

          <SubSection title="Traditional Finance Has Recovery Layers">
            <p className="text-muted-foreground mb-3">Banks:</p>
            <BulletList items={[
              "Reverse fraud",
              "Freeze transfers",
              "Mediate disputes"
            ]} />
            <p className="text-muted-foreground mt-3">
              <strong className="text-foreground">Blockchains generally do not.</strong>
            </p>
            <p className="text-lg font-medium text-red-600 dark:text-red-400 mt-3">
              Once private key control is lost, assets are gone.
            </p>
          </SubSection>

          <SubSection title="The Human Risk Layer">
            <p className="text-muted-foreground mb-3">
              The greatest risk in crypto is not technical failure. It is:
            </p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {["Lost context", "Poor documentation", "Assumed knowledge", "Fragmented storage"].map((risk, i) => (
                <div key={i} className="bg-foreground/5 rounded-lg p-3 text-center">
                  <span className="text-sm text-muted-foreground">{risk}</span>
                </div>
              ))}
            </div>
          </SubSection>

          <SubSection title="Your Executor Is Not a Crypto Native">
            <p className="text-muted-foreground mb-3">They may not understand:</p>
            <BulletList items={[
              "Gas fees",
              "Network selection",
              "Smart contract approvals",
              "Revoking token allowances",
              "Phishing risks"
            ]} />
            <p className="text-muted-foreground mt-3 font-medium">
              Documentation must assume <strong className="text-foreground">zero baseline knowledge.</strong>
            </p>
          </SubSection>

          <SubSection title="What Structured Clarity Looks Like">
            <BulletList items={[
              "Clear wallet categorisation",
              "Chain identification",
              "Asset priority ranking",
              "Recovery path location",
              "Risk flags",
              "Technical dependency notes"
            ]} />
            <p className="text-muted-foreground mt-4 italic">
              Not secrets. Not keys. Not seed phrases. <strong className="text-foreground">Just a map.</strong>
            </p>
          </SubSection>

          <SubSection title="The Lifely Principle">
            <div className="bg-primary/5 rounded-xl p-6 border border-primary/20">
              <p className="text-lg font-medium text-foreground mb-2">
                Security protects assets today.
              </p>
              <p className="text-lg font-medium text-foreground mb-4">
                Documentation protects them tomorrow.
              </p>
              <p className="text-xl font-serif font-semibold text-primary">
                You need both.
              </p>
            </div>
            <div className="bg-amber-50 dark:bg-amber-950/20 rounded-lg p-3 border border-amber-200 dark:border-amber-800 mt-4">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                <strong>Important:</strong> Blockchain ecosystems evolve rapidly. Always validate current network 
                procedures, smart contract risks, and custody standards with appropriate technical and legal professionals.
              </p>
            </div>
          </SubSection>
        </GuideSection>
  </div>
);

const EstatePlanningTab = () => (
  <div className="space-y-4">
    {/* Introduction */}
    <Card className="border border-border bg-card">
      <CardContent className="p-6">
        <div className="flex gap-4">
          <Info className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
          <div className="space-y-2">
            <h3 className="font-semibold text-foreground">About These Guides</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              These guides provide an overview of estate planning and will requirements in various jurisdictions. 
              They cover who can make a will, key legal requirements, the process of creating and executing a will, 
              and what happens after death. Select your region below to learn more.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Country Guides */}
    <h2 className="text-xl font-serif font-medium text-foreground flex items-center gap-2 mb-4">
      <Globe className="w-5 h-5 text-primary" />
      Estate Planning Guides by Region
    </h2>

    {/* United States */}
    <GuideSection title="United States" icon={Scale}>
          <SubSection title="Applicable Laws">
            <p className="mb-3">
              In the United States, wills and estates are governed by <strong>state law</strong> (there is no single national statute). 
              Most states follow similar core principles, often influenced by the Uniform Probate Code (UPC), which provides model rules 
              for wills and the probate process. This means requirements for a valid will are broadly consistent across the country, 
              though details can vary by state.
            </p>
          </SubSection>

          <SubSection title="Who Can Make a Will">
            <p className="mb-3">
              Generally, any person <strong>18 years or older</strong> and of sound mind can make a will in the U.S. "Sound mind" means 
              you understand what a will is, what property you have, and who your relatives or beneficiaries are. You must be acting 
              freely and not under undue influence.
            </p>
            <p className="text-xs text-muted-foreground/70 italic">
              A few states allow younger people to make wills in special cases, such as minors in the military or those who are married.
            </p>
          </SubSection>

          <SubSection title="Key Requirements for a Valid Will">
            <BulletList items={[
              "Written document: The will must be written (typed or handwritten). Oral wills are not accepted in most states.",
              "Signed by the Testator: You must sign the will, ideally at the end of the document.",
              "Witnesses: At least two witnesses must witness your signature and then sign the will themselves. Witnesses should be adults (18+), of sound mind, and 'disinterested' (not beneficiaries).",
              "Voluntary and Free Will: Made without duress or coercion.",
              "Testamentary Intent: The document should clearly show it's intended to be your will.",
              "Notarization: Not required in most states, but a 'self-proving' affidavit signed before a notary can simplify probate."
            ]} />
          </SubSection>

          <SubSection title="Holographic Wills">
            <p>
              About half of U.S. states recognize <strong>holographic wills</strong>, wills entirely in the testator's handwriting, 
              signed by them, with no witnesses. However, these are more easily challenged. It's best practice to execute a formal 
              typed will with witnesses whenever possible.
            </p>
          </SubSection>

          <SubSection title="Steps to Make a Will">
            <BulletList items={[
              "Take Inventory of Your Assets: List major assets and decide who should receive them.",
              "Decide on Beneficiaries: Choose people or organizations who will inherit.",
              "Choose an Executor: Select a trusted person to carry out your will's instructions.",
              "Name Guardians for Minor Children (if applicable).",
              "Write Your Will Document: Use an attorney, online platform, or statutory form.",
              "Execute the Will Properly: Sign with two witnesses present.",
              "Safely Store the Will: Keep in a fireproof safe or with your attorney.",
              "Update Your Will Over Time: Review after major life events."
            ]} />
          </SubSection>

          <SubSection title="After Death - Probate Process">
            <p className="mb-3">
              When you die, the executor files the will with the local probate court. The court reviews the will, formally appoints 
              the executor, and issues "Letters Testamentary." The executor then:
            </p>
            <BulletList items={[
              "Gathers and inventories assets",
              "Manages and protects the estate",
              "Notifies and pays creditors",
              "Handles any estate taxes",
              "Distributes assets to beneficiaries",
              "Accounts to the court and closes the estate"
            ]} />
          </SubSection>

          <SubSection title="Value-Added Tips">
            <BulletList items={[
              "Communicate Your Plans: Let close family know you have a will and general wishes.",
              "Consider Professional Advice: For complex estates, consult an estate planning attorney.",
              "Keep Beneficiary Designations Updated: Review 401(k)s, IRAs, life insurance regularly.",
              "Guard Against Challenges: Follow proper formalities and document your reasoning.",
              "Store Other Key Documents: Keep an asset list, insurance policies, and deeds together."
            ]} />
          </SubSection>
        </GuideSection>

        {/* England & Wales */}
        <GuideSection title="England & Wales" icon={Scale}>
          <SubSection title="Applicable Laws">
            <p>
              The key law governing wills is the <strong>Wills Act 1837</strong>, along with updates from later legislation. 
              Other relevant laws include the Administration of Estates Act 1925 and the Inheritance (Provision for Family 
              and Dependants) Act 1975.
            </p>
          </SubSection>

          <SubSection title="Who Can Make a Will">
            <p>
              Anyone aged <strong>18 or over</strong> can make a will in England and Wales, as long as they do so freely and 
              have the mental capacity to understand what they are doing.
            </p>
          </SubSection>

          <SubSection title="Key Requirements for a Valid Will">
            <BulletList items={[
              "In Writing: Must be written (typed or handwritten).",
              "Signed by the Testator: Must be signed by you at the end of the document.",
              "Witnessed by Two People: Your signature must be made in the presence of two witnesses present at the same time, who then sign in your presence.",
              "Independent Witnesses: Witnesses must be adults (18+) and should NOT be beneficiaries or spouses of beneficiaries. If a beneficiary witnesses, their gift becomes void.",
              "Made Voluntarily: Must be your free choice without coercion.",
              "Date Recommended: Include the date of signing to avoid confusion."
            ]} />
          </SubSection>

          <SubSection title="Making a Will - Process">
            <BulletList items={[
              "Decide on Dispositions: Think about your estate and who should inherit.",
              "Choose Executors: Select 1-4 people you trust to administer your estate.",
              "Draft the Will: Use a solicitor, will-writing service, or DIY if simple.",
              "Execute the Will Properly: Sign with two independent witnesses present together.",
              "Store the Will Safely: With your solicitor, in a fireproof safe, or with the Probate Registry.",
              "Review and Update: Marriage generally revokes a will; divorce voids gifts to ex-spouse."
            ]} />
          </SubSection>

          <SubSection title="After Death - Grant of Probate">
            <p className="mb-3">
              The executor applies for a <strong>Grant of Probate</strong> from the Probate Registry. Once granted, they can 
              collect assets, pay debts and taxes, and distribute according to the will.
            </p>
            <p className="text-sm">
              <strong>Inheritance Tax (IHT):</strong> Currently 40% on estates above the nil rate band. Bequests to spouses 
              or charities are generally tax-free.
            </p>
          </SubSection>

          <SubSection title="Contesting a Will">
            <p>
              Wills can be contested on grounds of improper execution, lack of capacity, or undue influence. Under the 
              Inheritance Act 1975, certain people (spouse, dependent children) can claim the will didn't make "reasonable 
              provision" for them.
            </p>
          </SubSection>
        </GuideSection>

        {/* Scotland */}
        <GuideSection title="Scotland" icon={Scale}>
          <SubSection title="Applicable Laws">
            <p>
              Scotland's law is set out in the <strong>Requirements of Writing (Scotland) Act 1995</strong> and Scots common law. 
              Key differences from England: minimum age is <strong>12 years old</strong>, and only <strong>one witness</strong> is required.
            </p>
          </SubSection>

          <SubSection title="Who Can Make a Will">
            <p>
              In Scotland, anyone aged <strong>12 or over</strong> who is of sound mind can make a will. The testator must have 
              mental capacity and act freely without undue influence.
            </p>
          </SubSection>

          <SubSection title="Key Requirements for a Valid Will">
            <BulletList items={[
              "Writing: Must be in writing (typed or handwritten).",
              "Signature: Should be signed by the testator at the end, ideally on every page.",
              "One Witness Required: Only one competent witness (age 16+) is needed, who signs after witnessing your signature.",
              "Independent Witness: Should not be a beneficiary or spouse of a beneficiary.",
              "No Notarization Required: The witness's signature is sufficient."
            ]} />
          </SubSection>

          <SubSection title="Legal Rights - Important Distinction">
            <p className="mb-3">
              Scotland has unique <strong>"legal rights"</strong> - certain close relatives (spouse/civil partner and children) 
              can claim a portion of the estate regardless of the will. You cannot completely disinherit a spouse or child in 
              Scotland due to these rights to a share of movable estate.
            </p>
            <p className="text-sm italic">
              This is a complex area - consult a solicitor if planning unequal distributions to close relatives.
            </p>
          </SubSection>

          <SubSection title="After Death - Confirmation">
            <p>
              The executor applies to the Sheriff Court for a grant of <strong>Confirmation</strong> (Scotland's equivalent of probate). 
              Once issued, the executor can collect assets, pay debts and taxes, and distribute according to the will.
            </p>
          </SubSection>

          <SubSection title="Key Differences from England">
            <BulletList items={[
              "Minimum age: 12 (not 18)",
              "Only 1 witness required (not 2)",
              "Witness must be 16+ (not 18+)",
              "Marriage does NOT revoke a will in Scotland",
              "Legal rights mean you cannot fully disinherit spouse or children"
            ]} />
          </SubSection>
        </GuideSection>

        {/* Northern Ireland */}
        <GuideSection title="Northern Ireland" icon={Scale}>
          <SubSection title="Applicable Laws">
            <p>
              Northern Ireland has its own legislation but closely mirrors England & Wales due to common historical origin. 
              The Wills Act 1837 applies, and requirements are virtually identical to England & Wales.
            </p>
          </SubSection>

          <SubSection title="Who Can Make a Will">
            <p>
              Anyone <strong>18 or older</strong> can make a will in Northern Ireland, provided they do so voluntarily and 
              have mental capacity.
            </p>
          </SubSection>

          <SubSection title="Key Requirements for a Valid Will">
            <BulletList items={[
              "Written Will: Must be in writing (typed or handwritten).",
              "Signature of Testator: You must sign the will at the end.",
              "Two Witnesses: Two witnesses must be present when you sign and must sign the will as witnesses.",
              "Independent Witnesses: Adults (18+) who are not beneficiaries or spouses of beneficiaries.",
              "Capacity and Intention: Must have capacity and intent to make the will."
            ]} />
          </SubSection>

          <SubSection title="After Death - Grant of Probate">
            <p className="mb-3">
              The executor applies to the Probate Office (part of the High Court in NI) for a <strong>Grant of Probate</strong>. 
              UK inheritance tax applies to NI estates.
            </p>
            <p className="text-sm">
              Under the Inheritance (Provision for Family and Dependants) (NI) Order 1979, dependants can apply for provision 
              if the will didn't adequately provide for them.
            </p>
          </SubSection>

          <SubSection title="Intestacy">
            <p>
              If you don't leave a will, NI's intestacy rules apply (same as England's). Unmarried partners and step-children 
              get nothing under intestacy - making a will is essential to provide for them.
            </p>
          </SubSection>
        </GuideSection>

        {/* Canada */}
        <GuideSection title="Canada" icon={Scale}>
          <SubSection title="Applicable Laws">
            <p>
              Wills and estates are governed by <strong>provincial and territorial law</strong>, so rules vary slightly 
              depending on where you live (Ontario, British Columbia, Alberta, etc.). Despite differences, fundamental 
              requirements are consistent across Canada.
            </p>
          </SubSection>

          <SubSection title="Who Can Make a Will">
            <p className="mb-3">
              Generally, you must be at least the <strong>age of majority</strong> (18 in most provinces, 19 in BC, Nova Scotia, 
              Newfoundland and New Brunswick) and mentally capable.
            </p>
            <p className="text-sm">
              <strong>Exceptions:</strong> BC permits wills at 16. Ontario allows minors to make a will if married or in military. 
              Quebec allows 18, or 16 for notarial wills.
            </p>
          </SubSection>

          <SubSection title="Key Requirements for a Valid Will">
            <BulletList items={[
              "Written Document: Must be in writing (some provinces now accept electronic wills).",
              "Signature: Must be signed by the testator at the end of the document.",
              "Two Witnesses: Most provinces require two adult witnesses present at the same time.",
              "Independent Witnesses: Witnesses should not be beneficiaries or spouses of beneficiaries.",
              "Date: Strongly advised to date the will at signing."
            ]} />
          </SubSection>

          <SubSection title="Holographic Wills">
            <p>
              About half of Canadian provinces recognize <strong>holographic wills</strong> (entirely handwritten, signed, no witnesses). 
              Recognized in Quebec, Ontario, Alberta, Saskatchewan, Manitoba and others. However, they can be risky due to poor wording 
              and no witness testimony.
            </p>
          </SubSection>

          <SubSection title="Quebec - Special Rules">
            <p>
              Quebec's civil law allows three forms of wills:
            </p>
            <BulletList items={[
              "Notarial wills (made before a notary, no probate needed)",
              "Holographic wills (handwritten)",
              "Wills made in presence of two witnesses"
            ]} />
          </SubSection>

          <SubSection title="Provincial Variations">
            <BulletList items={[
              "British Columbia: Strong court powers to vary wills for spouses/children who feel unfairly left out.",
              "Electronic Wills: BC and Quebec allow electronically signed/witnessed wills with conditions.",
              "Marriage: In many provinces, marriage revokes a will unless made in contemplation of that marriage.",
              "Divorce: May revoke gifts to ex-spouse but varies by province."
            ]} />
          </SubSection>

          <SubSection title="After Death - Probate">
            <p className="mb-3">
              The executor applies to the provincial court for probate (Grant of Probate or in Ontario, "Certificate of 
              Appointment of Estate Trustee with a Will"). Probate fees vary by province.
            </p>
            <p className="text-sm">
              <strong>Family Provision:</strong> All provinces allow dependants to claim additional support if the will 
              didn't make "adequate provision" for them.
            </p>
          </SubSection>
        </GuideSection>

        {/* Australia */}
        <GuideSection title="Australia" icon={Scale}>
          <SubSection title="Applicable Laws">
            <p>
              Wills and estates are governed by <strong>state and territory laws</strong>. Each state (NSW, Victoria, Queensland, etc.) 
              has its own Succession Act or Wills Act. Despite being separate laws, they are largely similar due to harmonization efforts.
            </p>
          </SubSection>

          <SubSection title="Who Can Make a Will">
            <p className="mb-3">
              Anyone <strong>18 years or older</strong> can make a will, as long as they have testamentary capacity (sound mind) 
              and are acting voluntarily.
            </p>
            <p className="text-sm">
              <strong>Exceptions:</strong> Minors can make a will if married, or with court authorization in some jurisdictions.
            </p>
          </SubSection>

          <SubSection title="Key Requirements for a Valid Will">
            <BulletList items={[
              "In Writing: Must be written (typed or handwritten).",
              "Signature of Testator: Must sign at the end of the document.",
              "Two Witnesses: Must be witnessed by at least two people present at the same time.",
              "Independent Witnesses: Adults (18+) who are not beneficiaries. If a beneficiary witnesses, their gift may be void.",
              "Attestation Clause: Not required but very useful - creates presumption of proper execution."
            ]} />
          </SubSection>

          <SubSection title="Informal Wills">
            <p>
              Australian courts have a unique power to recognize a document as a will even if not properly executed, if the court 
              is satisfied it was meant to be the person's will. However, don't rely on this - comply with formal rules.
            </p>
          </SubSection>

          <SubSection title="Steps to Make a Will">
            <BulletList items={[
              "Plan Your Estate: List assets including superannuation considerations.",
              "Choose Executors: Pick 1+ trusted people to administer your estate.",
              "Guardians for Minors: Nominate guardians for children under 18.",
              "Draft the Will: Use a solicitor or will kit for simple estates.",
              "Execute with Two Witnesses: Sign in presence of two independent adult witnesses.",
              "Store Safely: With solicitor, in fireproof safe, or bank deposit box.",
              "Review Regularly: Marriage often revokes a will; divorce may revoke gifts to ex-spouse."
            ]} />
          </SubSection>

          <SubSection title="Family Provision">
            <p>
              Australian laws allow certain people (spouse, children, dependants) to claim they should have received more from 
              an estate. You cannot prevent someone from filing a family provision claim, but you can mitigate by providing 
              some level of provision or explaining your reasoning.
            </p>
          </SubSection>

          <SubSection title="After Death - Probate">
            <p>
              The executor applies for Probate to the Supreme Court of the relevant state. Once granted, they can collect assets 
              and distribute according to the will. Probate fees vary by state.
            </p>
          </SubSection>
        </GuideSection>

        {/* New Zealand */}
        <GuideSection title="New Zealand" icon={Scale}>
          <SubSection title="Applicable Laws">
            <p className="mb-3">
              A New Zealand will must meet the formal validity rules in the <strong>Wills Act 2007</strong> (writing, signing, witnessing). 
              New Zealand also has a statutory "rescue" mechanism where the court can validate a document as a will in some circumstances, useful 
              to know, but not a plan to rely on. The will you write is not always the final word: certain close family can make claims for 
              proper provision under the <strong>Family Protection Act 1955</strong>, and people can claim for promises to reward services 
              under the <strong>Law Reform (Testamentary Promises) Act 1949</strong>.
            </p>
          </SubSection>

          <SubSection title="Who Can Make a Will">
            <p className="mb-3">
              The default rule is that a person aged <strong>18 or over</strong> may make, change, revoke, and revive a will. The Act also 
              provides defined situations where a person under 18 can make a will (for example, where they are in a recognised relationship 
              status or with court involvement).
            </p>
            <p>
              "Capacity" means you need to understand what a will does, what you own, and who you are choosing to benefit. If health or 
              pressure from others is a concern, the safest approach is lawyer-supervised drafting and signing with clean records.
            </p>
          </SubSection>

          <SubSection title="Formal Requirements">
            <BulletList items={[
              "Will must be in writing",
              "Signed/witnessed correctly",
              "Two adult witnesses present together during signing",
              "Both witnesses sign during the same signing session in your presence",
              "Notarisation is not required for a New Zealand will",
              "No general 'email/video will' system - assume physical presence witnessing"
            ]} />
          </SubSection>

          <SubSection title="Special Rules to Know">
            <p className="mb-3">
              <strong>Marriage/Civil Union:</strong> A will can be revoked automatically if you marry or enter a civil union, with limited 
              exceptions (the most common is making the will "in contemplation of" that marriage/civil union).
            </p>
            <p className="mb-3">
              <strong>Relationship Property:</strong> Partners/spouses can have a relationship property election under the Property 
              (Relationships) Act 1976, which can change what is actually in the estate to distribute.
            </p>
            <p>
              <strong>Small Estates:</strong> If the estate is under NZ$40,000 (increased from $15,000 in 2025), a High Court grant may 
              not be required depending on asset type and the institution holding it.
            </p>
          </SubSection>

          <SubSection title="Step-by-Step Checklist">
            <BulletList items={[
              "List what you own and what you owe (include digital assets and accounts)",
              "Choose your executor(s) and a backup, and confirm they're willing",
              "Decide who will receive the 'residue' (everything not gifted specifically)",
              "If you have children, decide guardianship preferences (courts consider best interests)",
              "Draft the will in writing and add a clear revocation clause",
              "Sign with two independent adult witnesses present together; have them sign immediately",
              "Store the signed original securely and tell your executor where it is",
              "Review after life events (marriage/civil union is a mandatory review trigger)"
            ]} />
          </SubSection>
        </GuideSection>

        {/* Fiji */}
        <GuideSection title="Fiji" icon={Scale}>
          <SubSection title="Applicable Laws">
            <p className="mb-3">
              Fiji's will-making rules are primarily in the <strong>Wills Act (Cap. 59)</strong> and estate administration is governed 
              through the <strong>Succession, Probate and Administration Act (Cap. 60)</strong>. Fiji also has an explicit family 
              provision regime via the <strong>Inheritance (Family Provision) Act 2004</strong>.
            </p>
          </SubSection>

          <SubSection title="Who Can Make a Will">
            <p>
              Fiji's Wills Act states that a person <strong>not less than 18 years of age</strong> has capacity to make a will. 
              "Capacity" is typically framed as soundness of mind/understanding - member guidance should treat sudden late-life 
              changes as a risk factor.
            </p>
          </SubSection>

          <SubSection title="Formal Requirements">
            <BulletList items={[
              "Will must be in writing",
              "Signed/acknowledged in the presence of at least two witnesses present together",
              "Witnesses then attest and sign",
              "No routine notarisation requirement",
              "Will registration involves filing two originals plus required identification"
            ]} />
          </SubSection>

          <SubSection title="Special Rules to Know">
            <p className="mb-3">
              <strong>Marriage and Revocation:</strong> Under the Wills Act, a will can be revoked by subsequent marriage, meaning 
              marriage is a "must-review" trigger.
            </p>
            <p>
              <strong>Family Provision:</strong> Fiji's family provision legislation allows the court to make maintenance/provision 
              orders from an estate, so aim for clear drafting that won't look "surprising" to close dependants.
            </p>
          </SubSection>

          <SubSection title="Tax and Inheritance">
            <p>
              Fiji has <strong>abolished estate and gift duties</strong>. Focus should be on: (a) the deceased's final income tax, 
              and (b) transaction taxes during asset transfers (for example, capital gains tax where applicable).
            </p>
          </SubSection>
        </GuideSection>

        {/* Tonga */}
        <GuideSection title="Tonga" icon={Scale}>
          <SubSection title="Applicable Laws">
            <p className="mb-3">
              The <strong>Probate Act (Cap. 16)</strong> contains the core formalities for making a will and the court's administration 
              powers. Tonga's land regime is governed by the <strong>Land Act (Cap. 132)</strong>, which is central to how land interests devolve.
            </p>
            <p className="text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 rounded-lg">
              <strong>Important:</strong> Many families assume a will controls everything, but in Tonga, land interests can be life interests 
              subject to strict statutory succession rules separate from your will.
            </p>
          </SubSection>

          <SubSection title="Who Can Make a Will">
            <p>
              Tonga's Probate Act states a valid will must be made by a person who has attained <strong>age 16</strong> and is of 
              "sound disposing mind".
            </p>
          </SubSection>

          <SubSection title="Formal Requirements">
            <BulletList items={[
              "Will must be in writing",
              "Signed at the foot or end",
              "Signature made/acknowledged in presence of two or more witnesses present at the same time",
              "Witnesses attest and subscribe in the testator's presence",
              "A gift to an attesting witness is generally void - don't use beneficiaries as witnesses"
            ]} />
          </SubSection>

          <SubSection title="Special Rules to Know">
            <p className="mb-3">
              <strong>Marriage Revokes Will:</strong> Tonga's Probate Act provides that marriage revokes a will - treat marriage as a 
              mandatory will update trigger.
            </p>
            <p className="mb-3">
              <strong>Widow Protections:</strong> The Act contains explicit protections for a widow (including inheritance of a dwelling 
              house on a town allotment, crops and certain items) whether or not there is a will.
            </p>
            <p className="mb-3">
              <strong>Small Estates:</strong> The court may deal with estates not exceeding $500 without full probate/letters formalities.
            </p>
            <p>
              <strong>Creditor Notice:</strong> Intending applicants must publish a notice to creditors in "the Chronicle" at least 
              <strong> two months</strong> before applying for probate/letters. This materially affects timelines.
            </p>
          </SubSection>

          <SubSection title="Tax and Inheritance">
            <p>
              There is <strong>no inheritance or death tax</strong> in Tonga. However, fees and administration costs exist, and land 
              succession disputes can be expensive.
            </p>
          </SubSection>
        </GuideSection>

        {/* Cook Islands */}
        <GuideSection title="Cook Islands" icon={Scale}>
          <SubSection title="Applicable Laws">
            <p className="mb-3">
              The Cook Islands has an established High Court probate jurisdiction for grants of probate and letters of administration. 
              Land succession is a major component of "estate reality" in the Cook Islands, with clear Land Court succession steps 
              and indicative processing timeframes.
            </p>
          </SubSection>

          <SubSection title="Formal Requirements">
            <p className="mb-3">
              Use a <strong>conservative execution standard</strong> compatible with common Commonwealth will-formalities:
            </p>
            <BulletList items={[
              "Written will",
              "Signature at the end",
              "Two independent witnesses present together",
              "Witnesses sign during the same session",
              "Confirm any local formalities for special cases (e.g., land interests)"
            ]} />
          </SubSection>

          <SubSection title="Land Succession">
            <p className="mb-3">
              The Land Court succession pathway requires supporting documents, an appointment, application completion, attendance at 
              hearing dates via published lists, and then collection of the court order. Published processing time is approximately 
              <strong> 60 days</strong>.
            </p>
            <p>
              Court fees for land succession applications have explicit filing fees and "per additional land" increments - budget for these.
            </p>
          </SubSection>

          <SubSection title="Probate Fees">
            <p>
              High Court fees include a filing fee for notice of motion for probate/letters and sealing fees that scale with estate value 
              (tiers for estates not exceeding $20,000; $20,000-$50,000; $50,000-$100,000; and exceeding $100,000).
            </p>
          </SubSection>

          <SubSection title="Common Pitfalls">
            <BulletList items={[
              "Assuming a will alone transfers land",
              "Incomplete family documentation for land succession",
              "Delays because hearing dates and published lists are missed"
            ]} />
          </SubSection>
        </GuideSection>

        {/* South Africa */}
        <GuideSection title="South Africa" icon={Scale}>
          <SubSection title="Applicable Laws">
            <p className="mb-3">
              South Africa's core will validity rules sit in the <strong>Wills Act 7 of 1953</strong>. Intestacy is governed by the 
              <strong> Intestate Succession Act 81 of 1987</strong>. Estate administration is under the <strong>Administration of 
              Estates Act 66 of 1965</strong> and overseen by the Master of the High Court system.
            </p>
            <p>
              Maintenance claims can arise under the <strong>Maintenance of Surviving Spouses Act 27 of 1990</strong>, and marital 
              property regime is governed by the <strong>Matrimonial Property Act 88 of 1984</strong> - both materially affect what 
              is distributable under a will.
            </p>
          </SubSection>

          <SubSection title="Who Can Make a Will">
            <p>
              The Wills Act provides that a person aged <strong>16 or more</strong> may make a will unless mentally incapable of 
              appreciating the nature and effect of the act at the time.
            </p>
          </SubSection>

          <SubSection title="Formal Requirements">
            <BulletList items={[
              "Will signed at the end by the testator (or by another person in presence and direction)",
              "Two or more competent witnesses present at the same time",
              "Witnesses sign in the presence of the testator and each other",
              "Treat remote witnessing as unsafe unless a professional confirms validity"
            ]} />
          </SubSection>

          <SubSection title="Special Rules to Know">
            <p className="mb-3">
              <strong>Three-Month Post-Divorce Rule:</strong> If a person dies within three months of divorce/annulment, the will 
              is implemented as if the former spouse had died before dissolution, unless the will shows intention to benefit the 
              former spouse despite the divorce.
            </p>
            <p className="mb-3">
              <strong>Marital Property Regime:</strong> The Matrimonial Property Act sets out the accrual system framework. The 
              marital property regime determines whether assets are jointly owned or form part of an accrual calculation, changing 
              the estate's "real size".
            </p>
            <p>
              <strong>Estate Size Thresholds:</strong> If the estate exceeds R250,000, letters of executorship are issued. Below 
              R250,000, the Master may issue letters of authority.
            </p>
          </SubSection>

          <SubSection title="Estate Duty (Tax)">
            <p className="mb-3">
              South Africa charges Estate Duty on the dutiable value:
            </p>
            <BulletList items={[
              "20% on the first R30 million",
              "25% above R30 million",
              "Abatement of R3.5 million",
              "Estate duty is due within one year of death",
              "Executors can be personally liable if duty remains unpaid"
            ]} />
          </SubSection>

          <SubSection title="Step-by-Step Checklist">
            <BulletList items={[
              "Confirm your marital property regime and update your will accordingly",
              "Draft a will with clear executor appointment and backups",
              "Sign correctly with two competent witnesses present together; avoid beneficiaries as witnesses",
              "Review immediately after divorce - the three-month rule is a safety net, not a planning strategy",
              "On administration, report the estate and determine which letters process applies"
            ]} />
          </SubSection>
        </GuideSection>
  </div>
);

const PasswordManagersTab = () => (
  <div className="space-y-4">
    <p className="text-muted-foreground text-sm mb-4">
      Step-by-step guides for setting up emergency access or continuity planning with popular password managers.
    </p>

    {/* 1Password */}
    <GuideSection title="1Password Emergency Access" icon={FileText}>
          <SubSection title="Introduction">
            <p>
              1Password is a popular password manager that stores your passwords encrypted so only you (with your Master Password 
              and Secret Key) can decrypt them. Even 1Password cannot unlock your vault - that's by design. This is great for 
              security, but it means you need to take proactive steps for emergency access.
            </p>
          </SubSection>

          <SubSection title="Understanding 1Password Security">
            <p>
              1Password's zero-knowledge design means the company cannot help unlock your vault without your credentials. 
              If you haven't shared your Master Password or set up an account recovery method, no one else can get in. 
              You must take proactive steps for emergency access.
            </p>
          </SubSection>

          <SubSection title="1Password Families or Teams">
            <p className="mb-3">
              If you have a <strong>1Password Families</strong> account, there are built-in recovery options. A family organizer 
              can recover accounts of other family members. Make your spouse or adult child a family organizer so they can help 
              with account recovery if needed.
            </p>
            <p className="text-sm italic">
              Note: Recovery requires some participation, so it's not a complete "if I die" solution, but it's a step towards sharing.
            </p>
          </SubSection>

          <SubSection title="The Emergency Kit - Your Key to Digital Legacy">
            <p className="mb-3">
              When you set up 1Password, you receive a PDF called the <strong>Emergency Kit</strong>. This contains your sign-in 
              address, email, and the critical Secret Key, with a place to write your Master Password. This is essentially the 
              keys to your kingdom.
            </p>
            <BulletList items={[
              "Print a copy of the Emergency Kit and write your Master Password on it",
              "Store it in a secure location your trusted contact can access (home safe, safe deposit box, with your lawyer)",
              "Do NOT store it in an obvious place while you're alive",
              "Tell your trusted person where to find it and how to use it",
              "Update the kit if you change your Master Password or Secret Key"
            ]} />
            <p className="text-sm mt-3 italic">
              1Password notes: "It's the only time you should write down your Master Password, and store it securely."
            </p>
          </SubSection>

          <SubSection title="Create an Instruction Letter">
            <p>
              Prepare a letter to your loved one with guidance. List which devices or browsers you had 1Password on, and explain 
              how to access it. For example: "Use this Master Password and Secret Key to sign in at my.1password.com. Then you'll 
              see all my logins. Here are the most important ones you'll need..."
            </p>
          </SubSection>

          <SubSection title="Use Shared Vaults for Family Access">
            <p className="mb-3">
              Within 1Password Families, you can share certain vaults with certain people. Consider creating a vault called 
              "Emergency" containing critical credentials (email, financial accounts) and share it with your spouse or adult 
              child ahead of time.
            </p>
            <p className="text-sm">
              They won't need your Master Password to access shared vaults - they use their own 1Password login.
            </p>
          </SubSection>

          <SubSection title="Security Considerations">
            <BulletList items={[
              "Seal the Emergency Kit in an envelope marked 'Only to be opened upon my incapacity or death'",
              "Be very selective about who gets this power - they can access everything",
              "Don't share your Master Password while actively using your account unless necessary",
              "Consider that your executor should ideally have official status before moving funds"
            ]} />
          </SubSection>

          <SubSection title="1Password's Stance on Deceased Users">
            <p>
              1Password does not have an official "legacy contact" program due to their encryption model. They cannot assist 
              in accessing the vault without the Master Password or a family organizer recovery. They might allow billing 
              changes or closing an account with proof of death, but cannot unlock data. The Emergency Kit approach is 
              exactly what 1Password recommends.
            </p>
          </SubSection>
        </GuideSection>

        {/* LastPass */}
        <GuideSection title="LastPass Emergency Access" icon={FileText}>
          <SubSection title="Introduction">
            <p>
              LastPass offers a built-in <strong>Emergency Access</strong> feature designed for exactly this scenario. 
              You can designate trusted people who can request access to your vault if you're unavailable. LastPass uses 
              a waiting period system, if someone requests emergency access, you can decline if you're okay. If you don't 
              decline, access is granted after the wait time.
            </p>
          </SubSection>

          <SubSection title="Choose Your Emergency Contact(s)">
            <p>
              Identify who should access your LastPass vault in an emergency. This should be someone you trust completely. 
              You can add more than one emergency contact (up to 5), each with their own waiting period. Consider a primary 
              contact (like spouse) and a secondary (like adult child).
            </p>
          </SubSection>

          <SubSection title="Setting Up Emergency Access">
            <BulletList items={[
              "Log in to your LastPass vault",
              "Find 'Emergency Access' in the left menu or under your account email",
              "Under 'People I Trust', invite someone by their email address",
              "Set a wait period (1 hour to 30 days - common choices are 48 hours to 7 days)",
              "Send the invite - they'll need to accept and create a LastPass account if they don't have one"
            ]} />
          </SubSection>

          <SubSection title="How It Works">
            <p className="mb-3">When your emergency contact needs access:</p>
            <BulletList items={[
              "They request access through their LastPass ('People Who Trust Me')",
              "You receive an email and alert about the request",
              "If you're fine, you can decline within the waiting period",
              "If you don't respond (incapacitated/deceased), access is automatically granted",
              "They then see your vault contents in their LastPass account"
            ]} />
            <p className="text-sm mt-3">
              The encryption ensures the contact can't decrypt your vault until the timer expires and the system releases the key.
            </p>
          </SubSection>

          <SubSection title="Choosing Wait Time">
            <p>
              Choose a wait time that suits your needs. <strong>"Immediate"</strong> is not advisable as the contact could get 
              in anytime. <strong>Too long</strong> (30 days) could cause hardship when families need prompt access. 
              <strong>3 days to a week</strong> is common, enough time for you to see a request if you're alive, but not too 
              long for survivors to wait.
            </p>
          </SubSection>

          <SubSection title="Inform Your Emergency Contact">
            <p>Have a conversation with your contact. Let them know:</p>
            <BulletList items={[
              "You've set them up as your LastPass emergency contact",
              "How long the wait period is",
              "Under what circumstances to use it",
              "What you expect them to do with that access"
            ]} />
          </SubSection>

          <SubSection title="What Happens After Access is Granted">
            <p>
              Your contact will be able to see all your stored logins, secure notes, etc. They should focus on critical accounts: 
              banking, investments, bills, email, social media. They might export the data or keep it in LastPass temporarily. 
              Leave separate instructions listing key accounts or wishes.
            </p>
          </SubSection>
        </GuideSection>

        {/* Bitwarden */}
        <GuideSection title="Bitwarden Emergency Access" icon={FileText}>
          <SubSection title="Introduction">
            <p>
              Bitwarden is an excellent open-source password manager that offers an <strong>Emergency Access</strong> feature 
              for Premium users. You can designate trusted people who can request access to your vault, with options for 
              read-only access or full account takeover. You also set a waiting period.
            </p>
          </SubSection>

          <SubSection title="Requirements">
            <p className="mb-3">
              The Emergency Access feature requires <strong>Bitwarden Premium</strong> (also included in Family/Organization plans). 
              Your emergency contact does not need to be premium - they can be a free user.
            </p>
          </SubSection>

          <SubSection title="Access Options">
            <p className="mb-3">Bitwarden offers two permission levels:</p>
            <BulletList items={[
              "View-Only: Contact can see all vault contents but cannot make changes. They can use credentials to log in to accounts but can't delete items or change your Master Password.",
              "Takeover: Contact can take over your account entirely, resetting the Master Password and effectively becoming you. Best for permanent transfer (like death)."
            ]} />
            <p className="text-sm mt-3">
              For a spouse or executor, takeover might be fine. For a tech-savvy friend helping out, view-only may be enough.
            </p>
          </SubSection>

          <SubSection title="Setting Up Emergency Access">
            <BulletList items={[
              "Log in to Bitwarden web vault",
              "Go to Account Settings > Emergency Access",
              "Invite a user by email (they need a Bitwarden account)",
              "Choose Permission Level (View Only or Takeover)",
              "Set the Wait Time (1 day, 7 days, 14, 30 days, etc.)",
              "Save/Send Invite - they'll receive an email to accept"
            ]} />
          </SubSection>

          <SubSection title="How It Works">
            <p className="mb-3">When your contact needs access:</p>
            <BulletList items={[
              "They go to their Bitwarden vault under 'Emergency Access > People Who Trust Me'",
              "They click to Request Access",
              "You receive an email notification",
              "The countdown begins (e.g., 7 days)",
              "If you do nothing, Bitwarden automatically grants access",
              "If you decline within the period, the request is blocked"
            ]} />
          </SubSection>

          <SubSection title="Communication and Documentation">
            <p>Tell your emergency contact:</p>
            <BulletList items={[
              "How long the wait period is",
              "Whether they'll get view or full access",
              "Under what circumstances to use it",
              "Let other family members know who has emergency access"
            ]} />
          </SubSection>

          <SubSection title="Organizing Your Vault">
            <p>
              Ensure your vault is organized so someone else can navigate it. Consider creating a folder called "Estate" 
              or "Important" with critical logins. Tag or favorite key items like bank accounts and insurance. Create 
              secure notes with instructions.
            </p>
          </SubSection>

          <SubSection title="When Access is Granted">
            <BulletList items={[
              "View-Only: Contact can see all entries and copy credentials to log in. Cannot edit or delete (data preserved).",
              "Takeover: Contact essentially becomes you on Bitwarden, can export vault, change passwords, etc.",
              "For death cases, takeover is appropriate since you won't be logging in anymore."
            ]} />
          </SubSection>

          <SubSection title="Regular Maintenance">
            <p>
              Review your emergency contacts annually. Ensure email addresses are current and you still want them as contacts. 
              Verify your wait period still makes sense. If relationships change, update or remove contacts as needed.
            </p>
          </SubSection>
        </GuideSection>

        {/* Keeper */}
        <GuideSection title="Keeper Emergency Access" icon={FileText}>
          <SubSection title="Introduction">
            <p>
              Keeper is a password manager that offers Emergency Access allowing you to designate up to <strong>five trusted 
              individuals</strong> as emergency contacts. When configured, your contacts can request access to your vault, 
              and after a waiting period you set, they'll receive access if you haven't declined.
            </p>
          </SubSection>

          <SubSection title="Key Features">
            <BulletList items={[
              "Designate up to 5 emergency contacts",
              "Configurable delay period (up to three months)",
              "Timed request system with email notifications",
              "Contacts require Keeper accounts to be recipients",
              "Full vault access granted after waiting period"
            ]} />
          </SubSection>

          <SubSection title="Setting Up Emergency Access">
            <BulletList items={[
              "Go to Settings > Emergency Access in your Keeper vault",
              "Add trusted users by their email address",
              "Set the delay period (recommended: 7-30 days)",
              "Ensure sharing relationships exist where required",
              "Invite contacts - they'll need to accept and create a Keeper account if they don't have one"
            ]} />
          </SubSection>

          <SubSection title="How It Works">
            <p className="mb-3">
              When your emergency contact requests access, you receive a notification. If you don't respond within the waiting 
              period you set, they're granted access to your vault. This allows legitimate emergency access while giving you 
              time to decline inappropriate requests.
            </p>
          </SubSection>

          <SubSection title="Best Practices">
            <BulletList items={[
              "Choose a waiting period long enough to notice requests (7-14 days minimum)",
              "Organize your vault with clear folders for important accounts",
              "Tell your contacts what the delay period is and when to use it",
              "Review your emergency contacts periodically"
            ]} />
          </SubSection>
        </GuideSection>

        {/* NordPass */}
        <GuideSection title="NordPass Emergency Access" icon={FileText}>
          <SubSection title="Introduction">
            <p>
              NordPass offers Emergency Access with a distinctive <strong>7-day fixed window</strong> approach. Premium users can 
              grant Emergency Access (including to free users as recipients). If you take no action within 7 days of a request, 
              access is granted automatically.
            </p>
          </SubSection>

          <SubSection title="Key Features">
            <BulletList items={[
              "Fixed 7-day accept/decline window",
              "Premium required to grant Emergency Access (can be granted to free users)",
              "Automatic access if account holder takes no action",
              "Timed request system with notifications",
              "Access to passwords after inaction period"
            ]} />
          </SubSection>

          <SubSection title="Setting Up Emergency Access">
            <BulletList items={[
              "Open NordPass and go to Settings > Emergency Access",
              "Add your trusted contact's email address",
              "They'll receive an invitation to accept",
              "Once accepted, they can request access when needed"
            ]} />
          </SubSection>

          <SubSection title="How It Works">
            <p className="mb-3">
              When your contact requests access, you have exactly <strong>7 days</strong> to accept or decline. If you do 
              nothing, whether by choice or because you're unavailable, access is granted automatically after the 7-day window.
            </p>
            <p>
              Because the wait period is fixed at 7 days (not configurable), your risk mitigation should come from careful 
              contact selection and strong email/device security.
            </p>
          </SubSection>

          <SubSection title="Best Practices">
            <BulletList items={[
              "Only grant Emergency Access to highly trusted individuals",
              "Keep your email secure - requests come via email",
              "Inform your contact about the 7-day window",
              "Organize your vault so they can find critical passwords easily"
            ]} />
          </SubSection>
        </GuideSection>

        {/* Dashlane */}
        <GuideSection title="Dashlane Continuity Planning" icon={FileText}>
          <SubSection title="Introduction">
            <p>
              Dashlane uses a <strong>manual continuity approach</strong> rather than timed Emergency Access. You create an 
              encrypted export file (DASH format) and share the password separately. This gives you full control but requires 
              more operational discipline.
            </p>
          </SubSection>

          <SubSection title="How Dashlane Continuity Works">
            <p className="mb-3">
              Dashlane recommends creating a secure DASH export file and storing it (for example, on a USB drive) so the 
              trusted person doesn't have immediate access. You share the export password separately, ideally via a secure 
              note or in-person communication.
            </p>
          </SubSection>

          <SubSection title="Setting Up Continuity">
            <BulletList items={[
              "Export your vault as an encrypted DASH file",
              "Store the file securely (USB drive, secure location)",
              "Share the export password through a separate, secure channel",
              "Document where the file is stored and how to access it",
              "Update the export when passwords change significantly"
            ]} />
          </SubSection>

          <SubSection title="Alternative: Item Sharing">
            <p className="mb-3">
              Dashlane also allows individual item sharing with other Dashlane users. You can share specific critical 
              passwords (banking, utilities, insurance) with a trusted contact without giving them full vault access.
            </p>
            <BulletList items={[
              "Share critical items selectively",
              "Recipient needs a Dashlane account",
              "Access is immediate for shared items",
              "Good for ongoing shared account access"
            ]} />
          </SubSection>

          <SubSection title="Best Practices">
            <BulletList items={[
              "Version your exports - update when passwords change",
              "Store the export separately from the password",
              "Consider a sealed envelope for the password, stored with estate documents",
              "Test that your contact can actually import and use the file"
            ]} />
          </SubSection>
        </GuideSection>

        {/* RoboForm */}
        <GuideSection title="RoboForm Emergency Access" icon={FileText}>
          <SubSection title="Introduction">
            <p>
              RoboForm provides Emergency Access where you select trusted contacts who can obtain access after death, 
              incapacitation, or account recovery needs. You can add multiple contacts with <strong>variable waiting 
              periods</strong> and revoke access at any time.
            </p>
          </SubSection>

          <SubSection title="Key Features">
            <BulletList items={[
              "Multiple emergency contacts supported",
              "Variable waiting periods per contact",
              "Revocation available at any time",
              "Timed request with email notifications",
              "Access to RoboForm data after waiting period"
            ]} />
          </SubSection>

          <SubSection title="Setting Up Emergency Access">
            <BulletList items={[
              "Go to Emergency Access settings in RoboForm",
              "Add trusted contacts by email",
              "Set individual waiting periods for each contact",
              "Contacts receive invitations to accept",
              "Test that the workflow works as expected"
            ]} />
          </SubSection>

          <SubSection title="How It Works">
            <p className="mb-3">
              When a trusted contact requests access, you receive a notification. The countdown begins for the waiting 
              period you set for that contact. If you don't decline within the period, they're granted access.
            </p>
          </SubSection>

          <SubSection title="Best Practices">
            <BulletList items={[
              "Set different wait times for different contacts if needed",
              "Periodically verify emergency contact invites still work",
              "Inform contacts about when to use Emergency Access",
              "Review and update contacts annually"
            ]} />
          </SubSection>
        </GuideSection>

        {/* Proton Pass */}
        <GuideSection title="Proton Pass Emergency Access" icon={FileText}>
          <SubSection title="Introduction">
            <p>
              Proton offers Emergency Access for paid Proton plan users. You can designate up to <strong>five contacts</strong>, 
              and the wait time can be immediate or delayed (days to months). Emergency Access applies to your Proton account 
              and data within the Proton ecosystem.
            </p>
          </SubSection>

          <SubSection title="Key Features">
            <BulletList items={[
              "Up to 5 emergency contacts",
              "Customizable wait time (immediate to months)",
              "Paid Proton plan required",
              "Access to Proton account and associated data",
              "Covers Proton Mail, Proton Drive, and Proton Pass"
            ]} />
          </SubSection>

          <SubSection title="Setting Up Emergency Access">
            <BulletList items={[
              "Go to Account Settings in your Proton account",
              "Navigate to Emergency Access settings",
              "Add contacts (up to 5)",
              "Set the wait time for each contact",
              "Contacts receive invitations to accept"
            ]} />
          </SubSection>

          <SubSection title="Scope of Access">
            <p className="mb-3">
              Note that Proton Emergency Access is broader than "passwords only" - it provides access to your Proton account 
              and associated data across Proton services. This can include:
            </p>
            <BulletList items={[
              "Proton Pass passwords and secure notes",
              "Proton Mail emails",
              "Proton Drive files",
              "Account recovery capabilities"
            ]} />
          </SubSection>

          <SubSection title="Best Practices">
            <BulletList items={[
              "Consider the full scope of what you're granting access to",
              "Choose wait times appropriate to the sensitivity of your data",
              "Inform contacts about what they'll be able to access",
              "Review contacts periodically as relationships change"
            ]} />
          </SubSection>
        </GuideSection>

        {/* Password Vault Comparison */}
        <GuideSection title="Password Vault Comparison" icon={Shield}>
          <SubSection title="Comparing Emergency Access Features">
            <p className="mb-3">
              Different password managers handle emergency access in different ways. Here's a quick comparison to help 
              you understand your options:
            </p>
          </SubSection>

          <SubSection title="Timed Emergency Access (Built-in)">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 pr-4 font-medium">Vault</th>
                    <th className="text-left py-2 pr-4 font-medium">Wait Period</th>
                    <th className="text-left py-2 pr-4 font-medium">Access Type</th>
                    <th className="text-left py-2 font-medium">Plan Required</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-border/50">
                    <td className="py-2 pr-4">LastPass</td>
                    <td className="py-2 pr-4">Configurable (7-30 days)</td>
                    <td className="py-2 pr-4">Vault access</td>
                    <td className="py-2">Premium/Families</td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-2 pr-4">Bitwarden</td>
                    <td className="py-2 pr-4">Configurable (1-30 days)</td>
                    <td className="py-2 pr-4">View-only or Takeover</td>
                    <td className="py-2">Premium (grantor)</td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-2 pr-4">Keeper</td>
                    <td className="py-2 pr-4">Up to 3 months</td>
                    <td className="py-2 pr-4">Vault access</td>
                    <td className="py-2">Depends on product</td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-2 pr-4">NordPass</td>
                    <td className="py-2 pr-4">Fixed 7 days</td>
                    <td className="py-2 pr-4">Password access</td>
                    <td className="py-2">Premium (to grant)</td>
                  </tr>
                  <tr className="border-b border-border/50">
                    <td className="py-2 pr-4">RoboForm</td>
                    <td className="py-2 pr-4">Variable per contact</td>
                    <td className="py-2 pr-4">Data access</td>
                    <td className="py-2">Check plan</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Proton Pass</td>
                    <td className="py-2 pr-4">Immediate to months</td>
                    <td className="py-2 pr-4">Account access</td>
                    <td className="py-2">Paid Proton plan</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </SubSection>

          <SubSection title="Manual Continuity Approaches">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 pr-4 font-medium">Vault</th>
                    <th className="text-left py-2 pr-4 font-medium">Method</th>
                    <th className="text-left py-2 font-medium">Notes</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-border/50">
                    <td className="py-2 pr-4">1Password</td>
                    <td className="py-2 pr-4">Emergency Kit (PDF)</td>
                    <td className="py-2">Print, add password, store securely</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Dashlane</td>
                    <td className="py-2 pr-4">DASH export + separate password</td>
                    <td className="py-2">Store file and password separately</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </SubSection>

          <SubSection title="Recommended Settings">
            <p className="mb-3">
              For most people, we recommend:
            </p>
            <BulletList items={[
              "Wait period: 7-14 days (long enough to notice, short enough to be practical)",
              "Access type: View-only where available (safer default)",
              "Contacts: One primary + one backup",
              "Communication: Tell contacts what to expect and when to use it",
              "Paper backup: Keep a sealed recovery pack separate from your will"
            ]} />
          </SubSection>
        </GuideSection>

    {/* Footer Note */}
    <Card className="border border-border bg-muted/30">
      <CardContent className="p-6">
        <p className="text-sm text-muted-foreground text-center">
          These guides were last updated in February 2026. Laws and technology change regularly - always verify current 
          requirements with qualified professionals and check the latest documentation from your service providers.
        </p>
      </CardContent>
    </Card>
  </div>
);

export default Insights;
